import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { PATHS } from "../../routes/paths"; // <-- keep your import
import axios from "axios";
import {
  FaCalendarCheck,
  FaCreditCard,
  FaChartLine,
  FaUser,
  FaCut,
  FaStore,
} from "react-icons/fa";
import CustomerHeader from "../../components/CustomerHeader";
import { APP_LOGO_URL } from "../../config/branding";
import "./LoginStyles.css";
import "./PremiumAuth.css";

function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [unverifiedEmail, setUnverifiedEmail] = useState("");
  const [incompleteRegistrationEmail, setIncompleteRegistrationEmail] =
    useState("");
  const [resendMessage, setResendMessage] = useState("");
  const [registrationResendMessage, setRegistrationResendMessage] =
    useState("");
  const [resendingVerification, setResendingVerification] = useState(false);
  const [resendingRegistration, setResendingRegistration] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [registrationCooldown, setRegistrationCooldown] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  // Canonical role map
  const roleMap = {
    R1: PATHS.OWNER_DASHBOARD, // Shop Owner
    R2: PATHS.ADMIN_DASHBOARD, // Shop Admin
    R3: "/dashboard/professional", // All service professionals
    R4: "/dashboard/freelancer", // Independent freelancers
    R5: "/customer/dashboard", // Customer
    R6: PATHS.PORTAL_ADMIN_DASHBOARD, // Portal Admin
    R7: "/dashboard/booth-renter", // Booth Renter
    R8: PATHS.PORTAL_ADMIN_DASHBOARD, // Support Agent
    R9: "/dashboard/agent", // Referral agent: their own area
    // String-based roles
    professional: "/dashboard/professional",
    customer: "/customer/dashboard",
    owner: PATHS.OWNER_DASHBOARD,
    admin: PATHS.PORTAL_ADMIN_DASHBOARD,
  };

  // Check for custom redirect and email params
  const params = new URLSearchParams(location.search);
  const redirectParam = params.get("redirect"); // Get custom redirect path
  const emailParam = params.get("email"); // Pre-fill email if provided
  const noticeParam = params.get("notice");

  // Pre-fill email from query param
  React.useEffect(() => {
    if (emailParam && !email) {
      setEmail(emailParam);
    }
  }, [emailParam, email]);

  React.useEffect(() => {
    if (noticeParam) {
      setNotice(noticeParam);
    }
  }, [noticeParam]);

  React.useEffect(() => {
    if (resendCooldown <= 0) return;

    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldown]);

  React.useEffect(() => {
    if (registrationCooldown <= 0) return;

    const timer = setInterval(() => {
      setRegistrationCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [registrationCooldown]);

  const handleResendVerification = async () => {
    const targetEmail = (unverifiedEmail || email || "").trim();
    if (!targetEmail) {
      setError("Please enter your email first.");
      return;
    }

    setResendingVerification(true);
    setError("");
    setResendMessage("");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/customer/resend-verification`,
        { email: targetEmail },
      );

      setResendMessage(
        response.data?.message ||
          "Verification link sent. Please check your inbox.",
      );
      setResendCooldown(Number(response.data?.cooldownSeconds) || 60);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        "Unable to resend verification link. Please try again.";
      setError(message);
      if (err?.response?.data?.cooldownSeconds) {
        setResendCooldown(Number(err.response.data.cooldownSeconds));
      }
    } finally {
      setResendingVerification(false);
    }
  };

  const handleResendCompleteRegistration = async () => {
    const targetEmail = (incompleteRegistrationEmail || email || "").trim();
    if (!targetEmail) {
      setError("Please enter your email first.");
      return;
    }

    setResendingRegistration(true);
    setError("");
    setRegistrationResendMessage("");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/complete-registration/resend`,
        { email: targetEmail },
      );

      setRegistrationResendMessage(
        response.data?.message ||
          "Registration link sent. Please check your inbox.",
      );
      setRegistrationCooldown(Number(response.data?.cooldownSeconds) || 60);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        "Unable to resend registration link. Please try again.";
      setError(message);
      if (err?.response?.data?.cooldownSeconds) {
        setRegistrationCooldown(Number(err.response.data.cooldownSeconds));
      }
    } finally {
      setResendingRegistration(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setResendMessage("");
    setRegistrationResendMessage("");
    setUnverifiedEmail("");
    setIncompleteRegistrationEmail("");

    // Clear any stale session storage redirect from previous login attempts
    sessionStorage.removeItem("redirectAfterLogin");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/login`,
        { email, password, rememberMe },
      );
      const { user } = response.data;

      // Persist pending login context only; authenticated token is issued after OTP verify.
      sessionStorage.setItem(
        "ogestyle_pending_login",
        JSON.stringify({
          email,
          rememberMe: rememberMe === true,
        }),
      );

      // Persist role metadata only; actual auth token is set after OTP verification.
      localStorage.setItem("ogestyle_role", user.role);
      localStorage.setItem("ogestyle_roles", JSON.stringify(user.roles || []));
      localStorage.setItem(
        "ogestyle_hasCompletedSetup",
        user.hasCompletedSetup !== false ? "true" : "false",
      );

      // Check if user is a freelancer using userType/role without pre-OTP API calls.
      let isFreelancer = user.userType === "freelancer";
      if (!isFreelancer && user.role === "R4") {
        isFreelancer = true;
      }

      // NOTE: shopIds are NOT stored in localStorage for security
      // Components fetch shops from backend using JWT token instead

      // If owner hasn't completed shop setup, redirect to dashboard home
      // TODO: Dashboard should show "Complete Business Setup" modal that persists across all pages until setup is done
      if (
        (user.roles.includes("R1") || user.roles.includes("R2")) &&
        user.hasCompletedSetup === false
      ) {
        // Owner/Admin needs to complete shop setup - send to dashboard where modal will guide them
        navigate(
          `/verify-code?email=${encodeURIComponent(email)}&next=${encodeURIComponent(PATHS.OWNER_DASHBOARD)}`,
        );
        return;
      }

      // If multiple major roles AND completed setup, enforce OTP first, then choose-role
      if (user.hasMultipleMajor && user.hasCompletedSetup !== false) {
        navigate(
          `/verify-code?email=${encodeURIComponent(email)}&next=${encodeURIComponent("/choose-role")}`,
        );
        return;
      }

      // Normal single-role flow
      let redirectPath = roleMap[user.role] || "/";

      // Check for custom redirect parameter (e.g., from email verification)
      if (redirectParam) {
        redirectPath = redirectParam;
      }
      // Override for freelancers - separate dashboard
      else if (isFreelancer) {
        redirectPath = "/dashboard/freelancer";
      }

      // Always require OTP verification
      navigate(
        `/verify-code?email=${encodeURIComponent(email)}&redirect=${encodeURIComponent(redirectPath)}`,
      );
    } catch (err) {
      const apiMessage =
        err?.response?.data?.message === "Invalid credentials"
          ? "Wrong email or password. Please check and try again."
          : err?.response?.data?.message ||
            "Wrong email or password. Please check and try again.";

      if (err?.response?.data?.unverified) {
        setUnverifiedEmail(err?.response?.data?.email || email);
      }

      if (err?.response?.data?.incompleteRegistration) {
        setIncompleteRegistrationEmail(err?.response?.data?.email || email);
      }

      setError(apiMessage);
    }
  };

  const handleSocialLogin = (provider) => {
    const API_URL =
      process.env.REACT_APP_API_BASE_URL;
    const providerLower = provider.toLowerCase();

    // Open OAuth popup
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      `${API_URL}/api/auth/${providerLower}`,
      `${provider} Login`,
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`,
    );

    // Listen for OAuth callback
    const checkPopup = setInterval(() => {
      try {
        if (popup.closed) {
          clearInterval(checkPopup);
          return;
        }

        // Check if popup redirected to success URL
        const popupUrl = popup.location.href;
        if (popupUrl.includes("/auth/success")) {
          const url = new URL(popupUrl);
          const token = url.searchParams.get("token");

          if (token) {
            // Save token
            localStorage.setItem("ogestyle_token", token);

            // Close popup
            popup.close();
            clearInterval(checkPopup);

            // Redirect based on redirect param or default to home
            const redirectPath = redirectParam || "/";
            navigate(redirectPath);
          }
        } else if (popupUrl.includes("/auth/error")) {
          popup.close();
          clearInterval(checkPopup);
          setError(`${provider} authentication failed. Please try again.`);
        }
      } catch (err) {
        // Cross-origin errors are expected until redirect
      }
    }, 500);

    // Timeout after 5 minutes
    setTimeout(() => {
      clearInterval(checkPopup);
      if (popup && !popup.closed) {
        popup.close();
      }
    }, 300000);
  };

  return (
    <>
      <CustomerHeader />
      <div className="login-page og-premium-auth og-login" style={{ minHeight: "calc(100vh - 50px)" }}>
        {/* Left Panel - Branding */}
        <div className="login-branding">
          <div className="og-branding">
            <p className="og-eyebrow">Welcome back</p>
            <h2 className="og-brand-title">Pick up where you left off</h2>
            <p className="og-brand-sub">
              Book your next appointment, shop your favourite professionals, or
              run your business — all in one calm, connected place.
            </p>
            <div className="og-features">
              <div className="og-feature">
                <span className="og-feature-ic"><FaCalendarCheck /></span>
                <span>Book &amp; buy in one step</span>
              </div>
              <div className="og-feature">
                <span className="og-feature-ic"><FaCreditCard /></span>
                <span>Secure payments &amp; automatic payouts</span>
              </div>
              <div className="og-feature">
                <span className="og-feature-ic"><FaChartLine /></span>
                <span>Grow your business with insights</span>
              </div>
            </div>
            <div className="og-trust">
              <span>Payments secured by Stripe</span>
              <span>No app to download</span>
              <span>No hidden fees</span>
            </div>
          </div>
        </div>

        {/* Right Panel - Form */}
        <div className="login-form-panel">
          <div className="login-container">
            <div className="login-header">
              <div className="mobile-logo"><img src={APP_LOGO_URL} alt="OGESTYLE" style={{ maxWidth: "120px", height: "auto" }} /></div>
              <h2>Welcome Back</h2>
              <p className="subtitle">Sign in to continue to OGESTYLE</p>
            </div>

            {notice && (
              <div
                style={{
                  background: "#dbeafe",
                  border: "1px solid #93c5fd",
                  color: "#1e3a8a",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  marginBottom: "12px",
                  fontSize: "14px",
                }}
              >
                {notice}
              </div>
            )}

            {error && <div className="error-message">{error}</div>}

            {unverifiedEmail && (
              <div
                style={{
                  marginBottom: "12px",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: "1px solid #d1d5db",
                  background: "#f9fafb",
                }}
              >
                <div style={{ fontSize: "13px", color: "#374151" }}>
                  Didn't receive your verification email?
                </div>
                <button
                  type="button"
                  onClick={handleResendVerification}
                  disabled={resendingVerification || resendCooldown > 0}
                  style={{
                    marginTop: "8px",
                    border: "none",
                    borderRadius: "6px",
                    padding: "8px 12px",
                    background:
                      resendingVerification || resendCooldown > 0
                        ? "#94a3b8"
                        : "#1a659e",
                    color: "#fff",
                    cursor:
                      resendingVerification || resendCooldown > 0
                        ? "not-allowed"
                        : "pointer",
                    fontWeight: 600,
                    fontSize: "13px",
                  }}
                >
                  {resendingVerification
                    ? "Sending..."
                    : resendCooldown > 0
                      ? `Resend in ${resendCooldown}s`
                      : "Resend Verification Link"}
                </button>
                {resendMessage && (
                  <div
                    style={{
                      marginTop: "8px",
                      fontSize: "12px",
                      color: "#166534",
                    }}
                  >
                    {resendMessage}
                  </div>
                )}
              </div>
            )}

            {incompleteRegistrationEmail && (
              <div
                style={{
                  marginBottom: "12px",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: "1px solid #d1d5db",
                  background: "#f9fafb",
                }}
              >
                <div style={{ fontSize: "13px", color: "#374151" }}>
                  Need a new complete-registration link?
                </div>
                <button
                  type="button"
                  onClick={handleResendCompleteRegistration}
                  disabled={resendingRegistration || registrationCooldown > 0}
                  style={{
                    marginTop: "8px",
                    border: "none",
                    borderRadius: "6px",
                    padding: "8px 12px",
                    background:
                      resendingRegistration || registrationCooldown > 0
                        ? "#94a3b8"
                        : "#1a659e",
                    color: "#fff",
                    cursor:
                      resendingRegistration || registrationCooldown > 0
                        ? "not-allowed"
                        : "pointer",
                    fontWeight: 600,
                    fontSize: "13px",
                  }}
                >
                  {resendingRegistration
                    ? "Sending..."
                    : registrationCooldown > 0
                      ? `Resend in ${registrationCooldown}s`
                      : "Resend Complete-Registration Link"}
                </button>
                {registrationResendMessage && (
                  <div
                    style={{
                      marginTop: "8px",
                      fontSize: "12px",
                      color: "#166534",
                    }}
                  >
                    {registrationResendMessage}
                  </div>
                )}
              </div>
            )}

            <form onSubmit={handleLogin}>
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              <div className="form-row">
                <div className="remember-me-container">
                  <label>
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                    />
                    <span>Remember me</span>
                  </label>
                </div>
                <div className="forgot-password-link">
                  <a href="/forgot-password">Forgot Password?</a>
                </div>
              </div>

              <button type="submit" className="login-button">
                Sign In
              </button>
            </form>

            {/* TEMPORARILY HIDDEN - Social Media Login */}
            {/* <div className="divider">
              <span>or continue with</span>
            </div>

            <div className="social-login-container">
              <button
                type="button"
                className="oauth-button google"
                onClick={() => handleSocialLogin("Google")}
              >
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                <span>Google</span>
              </button>
              <button
                type="button"
                className="oauth-button facebook"
                onClick={() => handleSocialLogin("Facebook")}
              >
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path
                    fill="#1877F2"
                    d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
                  />
                </svg>
                <span>Facebook</span>
              </button>
              <button
                type="button"
                className="oauth-button apple"
                onClick={() => handleSocialLogin("Apple")}
              >
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
                  />
                </svg>
                <span>Apple</span>
              </button>
            </div> */}

            <div className="register-section">
              <p className="register-text">Don't have an account?</p>
              <div className="register-options">
                <button
                  className="register-option customer"
                  onClick={() => navigate("/customer-signup")}
                >
                  <span className="icon"><FaUser /></span>
                  <span className="text">Customer</span>
                </button>
                <button
                  className="register-option freelancer"
                  onClick={() => navigate("/freelancer/signup")}
                >
                  <span className="icon"><FaCut /></span>
                  <span className="text">Freelancer</span>
                </button>
                <button
                  className="register-option owner"
                  onClick={() => navigate("/register-business")}
                >
                  <span className="icon"><FaStore /></span>
                  <span className="text">Business</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default LoginPage;
